import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiClient } from "@/lib/api";
import { Panel, PanelHeader } from "@/components/ui-bits";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Settings2, Plus, Trash2, IndianRupee, Coins } from "lucide-react";
import { INR } from "@/lib/constants";
import { toast } from "sonner";

const CATEGORY_LABEL = {
  MERCH: "Merchandise",
  PT: "PT Session",
  RECOVERY: "Recovery Session",
  GUEST_PASS: "Guest Pass",
  CASH: "Cash Equivalent",
};
const CATEGORY_COLOR = {
  MERCH: "#CCFF00",
  PT: "#FF6B35",
  RECOVERY: "#7DD3FC",
  GUEST_PASS: "#A78BFA",
  CASH: "#00E676",
};

export default function RewardSettingsPage() {
  const qc = useQueryClient();
  const { data: cfg } = useQuery({
    queryKey: ["reward-config"],
    queryFn: async () => (await apiClient.get("/reward-config")).data,
  });
  const { data: items = [] } = useQuery({
    queryKey: ["redemption-items"],
    queryFn: async () => (await apiClient.get("/redemption-items")).data,
  });

  const [form, setForm] = useState(null);
  React.useEffect(() => { if (cfg && !form) setForm(cfg); }, [cfg]); // eslint-disable-line

  const save = useMutation({
    mutationFn: async (body) => (await apiClient.put("/reward-config", body)).data,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["reward-config"] }); toast.success("Rewards config saved"); },
  });

  const removeItem = useMutation({
    mutationFn: async (id) => (await apiClient.delete(`/redemption-items/${id}`)).data,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["redemption-items"] }); toast.success("Item removed"); },
  });

  if (!form) return <div className="p-8 text-zinc-500">Loading…</div>;

  return (
    <div className="p-8 space-y-6 animate-fade-in-up" data-testid="rewards-settings-page">
      <div>
        <div className="text-[10px] uppercase tracking-[0.3em] text-volt font-semibold flex items-center gap-2"><Settings2 size={12} /> Rewards Engine</div>
        <h1 className="font-display text-5xl font-black uppercase tracking-tight mt-1">{form.currency_name || "REPS"} · Configuration</h1>
        <div className="text-zinc-500 text-sm mt-1">Set how members and coaches earn REPS · define what they can redeem for.</div>
      </div>

      {/* Config */}
      <Panel>
        <PanelHeader title="Earn Rate & Value" subtitle="Affects future conversions only" />
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Field label="Member earn %" hint="of plan value on referral conversion">
            <Input data-testid="member-pct" type="number" step="0.5" value={form.member_earn_pct}
              onChange={(e) => setForm({ ...form, member_earn_pct: Number(e.target.value) })}
              className="bg-zinc-950 border-zinc-800" />
          </Field>
          <Field label="Member minimum REPS" hint="floor for any conversion">
            <Input data-testid="member-min" type="number" value={form.member_min_reps}
              onChange={(e) => setForm({ ...form, member_min_reps: Number(e.target.value) })}
              className="bg-zinc-950 border-zinc-800" />
          </Field>
          <div></div>
          <Field label="Coach earn %" hint="of plan value">
            <Input data-testid="coach-pct" type="number" step="0.5" value={form.coach_earn_pct}
              onChange={(e) => setForm({ ...form, coach_earn_pct: Number(e.target.value) })}
              className="bg-zinc-950 border-zinc-800" />
          </Field>
          <Field label="Coach minimum REPS" hint="floor for any conversion">
            <Input data-testid="coach-min" type="number" value={form.coach_min_reps}
              onChange={(e) => setForm({ ...form, coach_min_reps: Number(e.target.value) })}
              className="bg-zinc-950 border-zinc-800" />
          </Field>
          <div></div>
          <Field label={`₹ value per ${form.currency_name || "REP"}`} hint="used to compute redemption ₹ equivalence">
            <Input data-testid="rupee-per-rep" type="number" step="0.1" value={form.rupee_value_per_rep}
              onChange={(e) => setForm({ ...form, rupee_value_per_rep: Number(e.target.value) })}
              className="bg-zinc-950 border-zinc-800" />
          </Field>
          <Field label="Currency name" hint="display label across the app">
            <Input data-testid="currency-name" value={form.currency_name}
              onChange={(e) => setForm({ ...form, currency_name: e.target.value })}
              className="bg-zinc-950 border-zinc-800 font-display uppercase" />
          </Field>
          <div className="flex items-end">
            <Button onClick={() => save.mutate(form)} disabled={save.isPending} data-testid="save-config"
              className="w-full bg-volt hover:bg-volt-hover text-black font-bold uppercase tracking-wider rounded-sm">
              {save.isPending ? "Saving…" : "Save Config"}
            </Button>
          </div>
        </div>

        <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-3">
          <div className="bg-zinc-950 border border-zinc-900 rounded-sm p-3 text-xs">
            <div className="text-[10px] uppercase tracking-[0.16em] text-zinc-500 mb-1">Example · Member referral</div>
            <div className="text-zinc-300">On a ₹10,000 plan conversion, member earns
              <span className="text-volt font-display text-xl ml-2">
                {Math.max(form.member_min_reps, Math.round(10000 * form.member_earn_pct / 100))} {form.currency_name}
              </span>
              <span className="text-zinc-500 ml-2">(≈ ₹{(Math.max(form.member_min_reps, Math.round(10000 * form.member_earn_pct / 100)) * form.rupee_value_per_rep).toFixed(0)} value)</span>
            </div>
          </div>
          <div className="bg-zinc-950 border border-zinc-900 rounded-sm p-3 text-xs">
            <div className="text-[10px] uppercase tracking-[0.16em] text-zinc-500 mb-1">Example · Coach referral</div>
            <div className="text-zinc-300">On a ₹10,000 plan conversion, coach earns
              <span className="text-volt font-display text-xl ml-2">
                {Math.max(form.coach_min_reps, Math.round(10000 * form.coach_earn_pct / 100))} {form.currency_name}
              </span>
              <span className="text-zinc-500 ml-2">(≈ ₹{(Math.max(form.coach_min_reps, Math.round(10000 * form.coach_earn_pct / 100)) * form.rupee_value_per_rep).toFixed(0)} value)</span>
            </div>
          </div>
        </div>
      </Panel>

      {/* Catalog */}
      <Panel>
        <div className="flex items-center justify-between mb-4">
          <PanelHeader title="Redemption Catalog" subtitle={`${items.length} items · members & coaches use REPS to redeem`} />
          <NewItemDialog onSuccess={() => qc.invalidateQueries({ queryKey: ["redemption-items"] })} />
        </div>
        {items.length === 0 ? (
          <div className="text-zinc-600 text-sm py-12 text-center">No items yet.</div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {items.map((it) => (
              <div key={it.id} data-testid={`item-${it.id}`} className="bg-zinc-950 border border-zinc-900 rounded-sm p-4 flex flex-col">
                <div className="flex items-start justify-between">
                  <div>
                    <span className="text-[10px] uppercase tracking-[0.18em] font-bold px-1.5 py-0.5 rounded-sm"
                      style={{ background: `${CATEGORY_COLOR[it.category]}22`, color: CATEGORY_COLOR[it.category] }}>
                      {CATEGORY_LABEL[it.category] || it.category}
                    </span>
                  </div>
                  <button onClick={() => removeItem.mutate(it.id)} data-testid={`delete-${it.id}`}
                    className="text-zinc-500 hover:text-red-400" title="Remove">
                    <Trash2 size={14} />
                  </button>
                </div>
                <div className="font-display text-xl font-black uppercase tracking-tight mt-2">{it.name}</div>
                <div className="text-xs text-zinc-400 mt-1 flex-1">{it.description}</div>
                <div className="mt-3 flex items-baseline justify-between">
                  <div className="font-display text-3xl font-black text-volt">
                    {it.reps_cost} <span className="text-xs text-zinc-500 font-sans">{form.currency_name}</span>
                  </div>
                  <div className="text-[10px] text-zinc-500">≈ ₹{Math.round(it.reps_cost * form.rupee_value_per_rep)}</div>
                </div>
              </div>
            ))}
          </div>
        )}
      </Panel>
    </div>
  );
}

function Field({ label, hint, children }) {
  return (
    <div>
      <Label className="text-[10px] uppercase tracking-[0.16em] text-zinc-500">{label}</Label>
      <div className="mt-1">{children}</div>
      {hint && <div className="text-[10px] text-zinc-600 mt-1">{hint}</div>}
    </div>
  );
}

function NewItemDialog({ onSuccess }) {
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ name: "", description: "", category: "MERCH", reps_cost: 500 });
  const submit = async () => {
    if (!f.name || !f.reps_cost) { toast.error("Name + REPS cost required"); return; }
    try {
      await apiClient.post("/redemption-items", { ...f, reps_cost: Number(f.reps_cost) });
      toast.success("Item added");
      setOpen(false); onSuccess?.();
      setF({ name: "", description: "", category: "MERCH", reps_cost: 500 });
    } catch (e) { toast.error("Failed"); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button data-testid="new-item-button" className="bg-volt hover:bg-volt-hover text-black font-bold uppercase tracking-wider rounded-sm">
          <Plus size={14} className="mr-1" /> New Item
        </Button>
      </DialogTrigger>
      <DialogContent className="bg-[#0a0a0a] border-zinc-800 text-white max-w-md" data-testid="new-item-dialog">
        <DialogHeader><DialogTitle className="font-display text-2xl uppercase">New Redemption Item</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <Field label="Name *"><Input data-testid="item-name" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} className="bg-zinc-950 border-zinc-800" /></Field>
          <Field label="Description"><Input value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} className="bg-zinc-950 border-zinc-800" /></Field>
          <Field label="Category">
            <Select value={f.category} onValueChange={(v) => setF({ ...f, category: v })}>
              <SelectTrigger className="bg-zinc-950 border-zinc-800"><SelectValue /></SelectTrigger>
              <SelectContent>{Object.entries(CATEGORY_LABEL).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
            </Select>
          </Field>
          <Field label="REPS cost *"><Input data-testid="item-cost" type="number" value={f.reps_cost} onChange={(e) => setF({ ...f, reps_cost: e.target.value })} className="bg-zinc-950 border-zinc-800" /></Field>
          <Button onClick={submit} data-testid="save-item" className="w-full bg-volt hover:bg-volt-hover text-black font-bold uppercase tracking-wider">Add</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
